import React from 'react';
import ArcGisLocationDisplay from './ArcGisLocationDisplay';
import {
    Alert,
    Button,
    FormGroup,
    Input,
    Modal,
    ModalBody,
    ModalHeader,
} from 'reactstrap';
import moment from 'moment';
import { MomentDateFormat } from '../MomentDateFormat';
import { FormLabel } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTriangleExclamation,
    faCloudArrowUp,
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import { ApiRoutes } from '../ApiRoutes';
import { toasty } from '../common/forms/FormElements';
import { util } from '../Util';
import FaqtsLocationDisplay from './FaqtsLocationDisplay';
import cls from 'classnames';

export default class LowboyEquipmentLocationButton extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            showModal: false,
            knownLocation: null,
            timeOfDay: '',
        };
    }

    toggleModal = () => {
        const { enableAddressChange, equipment } = this.props;

        if (!enableAddressChange || !equipment) {
            return;
        }

        const { showModal } = this.state;
        this.setState({ showModal: !showModal });
    };

    setKnownLocation = (knownLocation) => {
        this.setState({ knownLocation });
    };

    onUpdateEquipmentLocation = async () => {
        const { activeDate, equipment, onEquipmentLocationUpdated } =
            this.props;
        const { timeOfDay, knownLocation } = this.state;

        const splitTime = timeOfDay.split(':');

        const moveRequestedFor = moment(activeDate)
            .set('hour', splitTime[0])
            .set('minute', splitTime[1])
            .toISOString();

        try {
            const response = await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.equipmentLocation.move(),
                {
                    equipmentId: equipment.id,
                    moveRequestedFor,
                    destinationLocationId: knownLocation.locationId,
                    destinationArcGisLocationId: knownLocation.arcGisLocationId,
                }
            );

            if (response) {
                toasty.success('Equipment location updated successfully.');

                onEquipmentLocationUpdated();

                this.setState({
                    showModal: false,
                    knownLocation: null,
                    timeOfDay: '',
                });
            }
        } catch (e) {
            console.error({ e });
            toasty.error('Failed to update equipment location.');
        }
    };

    getFaqtsLocation = (equipmentLocation) => {
        if (!equipmentLocation) {
            return null;
        }

        if (
            moment(equipmentLocation.moveRequestedFor).isSame(new Date(), 'day')
        ) {
            return equipmentLocation?.originLocation;
        }

        return equipmentLocation.destinationLocation
            ? equipmentLocation.destinationLocation
            : equipmentLocation?.originLocation;
    };

    getArcGisLocation = (equipmentLocation) => {
        if (!equipmentLocation) {
            return null;
        }

        if (
            moment(equipmentLocation.moveRequestedFor).isSame(new Date(), 'day')
        ) {
            return equipmentLocation?.originArcGisLocation;
        }

        return equipmentLocation.destinationArcGisLocation
            ? equipmentLocation.destinationArcGisLocation
            : equipmentLocation?.originArcGisLocation;
    };

    render() {
        const {
            equipment,
            equipmentLocations,
            equipmentTypes,
            activeDate,
            knownLocations,
        } = this.props;

        const { showModal, knownLocation, timeOfDay } = this.state;

        const timeOfDayAvailable = Boolean(knownLocation);

        const isValid = Boolean(timeOfDayAvailable && timeOfDay);

        if (!equipment) {
            return null;
        }

        const equipmentLocation = equipmentLocations.find(
            (el) => el.equipmentId == equipment.id
        );

        const equipmentType = equipmentTypes.find(
            (et) => equipment.equipmentTypeId == et.id
        );

        const faqtsLocation = this.getFaqtsLocation(equipmentLocation);

        const arcGisLocation = this.getArcGisLocation(equipmentLocation);

        const noKnownLocation = !faqtsLocation && !arcGisLocation;

        return (
            <>
                <div
                    onClick={this.toggleModal}
                    className={cls('equipment-location-container', {
                        'danger-div': noKnownLocation,
                    })}
                >
                    <label className={cls({ 'danger-label': noKnownLocation })}>
                        {faqtsLocation && (
                            <FaqtsLocationDisplay
                                location={faqtsLocation}
                                compact={true}
                            />
                        )}

                        {!faqtsLocation && (
                            <ArcGisLocationDisplay location={arcGisLocation} />
                        )}
                    </label>
                </div>

                <Modal isOpen={showModal} toggle={this.toggleModal} size="lg">
                    <ModalHeader toggle={this.toggleModal}>
                        Equipment Location Management
                    </ModalHeader>
                    <ModalBody>
                        <Alert
                            className="d-flex font-size-lg mb-3"
                            fade={false}
                            color="warning"
                        >
                            <div>
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    className="mr-2"
                                />
                            </div>
                            <div>
                                You are setting the location for{' '}
                                {equipmentType.description} #
                                {equipment.identifier} {equipment.description}{' '}
                                for{' '}
                                {moment(activeDate).format(
                                    MomentDateFormat.WeekDayNameAndDate
                                )}
                                .
                            </div>
                        </Alert>

                        <div>
                            <FormGroup>
                                <FormLabel>Location</FormLabel>

                                <Select
                                    options={knownLocations}
                                    onChange={this.setKnownLocation}
                                    getOptionLabel={(kl) => kl.description}
                                    getOptionValue={(kl) => kl.description}
                                    value={knownLocation}
                                />
                            </FormGroup>

                            <FormGroup>
                                <FormLabel> Time of Day</FormLabel>

                                <Input
                                    type="time"
                                    value={timeOfDay}
                                    onChange={(e) =>
                                        this.setState({
                                            timeOfDay: e.target.value,
                                        })
                                    }
                                    disabled={!timeOfDayAvailable}
                                />
                            </FormGroup>

                            <FormGroup>
                                <Button
                                    type="button"
                                    color="success"
                                    disabled={!isValid}
                                    outline={!isValid}
                                    onClick={this.onUpdateEquipmentLocation}
                                >
                                    <FontAwesomeIcon
                                        icon={faCloudArrowUp}
                                        className="mr-2"
                                    />
                                    Update Location
                                </Button>
                            </FormGroup>
                        </div>
                    </ModalBody>
                </Modal>
            </>
        );
    }
}
